import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Ban } from 'lucide-react';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import VoidConfirmationModal from '../../components/VoidConfirmationModal';
import {
  getInventoryAdjustmentById,
  voidInventoryAdjustment,
} from '../../utils/inventoryAdjustmentApi';
import { ApiValidationError } from '../../types/auth';
import type { InventoryAdjustment } from '../../types/inventoryAdjustment';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { toast } from '../../utils/toast';
import { adjustmentVoidCopy } from '../../utils/voidConfirmationCopy';
import {
  canVoidAdjustment,
  formatAdjustmentDate,
  formatAdjustmentLabel,
  formatAdjustmentTime,
  getAdjustmentStatusColor,
} from '../../utils/inventoryAdjustmentForm';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';

const InventoryAdjustmentPreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');

  const adjustmentId = id != null ? Number(id) : Number.NaN;

  const [detail, setDetail] = useState<InventoryAdjustment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const close = useCallback(() => {
    navigate({ pathname: '/inventory-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const loadDetail = useCallback(async () => {
    if (!id || Number.isNaN(adjustmentId)) {
      setError('Invalid adjustment ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDetail(await getInventoryAdjustmentById(adjustmentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load adjustment');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [adjustmentId, id]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async (reason: string) => {
    if (!canVoidAdjustment() || !detail) return;

    try {
      setVoiding(true);
      setVoidError(null);
      const voided = await voidInventoryAdjustment(detail.id, {
        void_reason: reason || null,
      });
      setShowVoidModal(false);
      setDetail(voided);
      refetchList();
      toast.success(`${voided.adjustment_number} voided.`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setVoidError(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to void adjustment');
      }
    } finally {
      setVoiding(false);
    }
  };

  const canVoidDetail =
    detail != null && detail.status !== 'VOIDED' && canVoidAdjustment();

  const totalValue =
    detail?.lines.reduce((sum, line) => sum + line.value_change, 0) ?? 0;

  return (
    <>
    <TransactionOverlay
      variant="preview"
      title={detail?.adjustment_number ?? 'Adjustment'}
      subtitle={detail ? `Adjustment · ${formatAdjustmentLabel(detail.status ?? 'OPEN')}` : 'Loading…'}
      onClose={close}
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => void loadDetail()} className="underline shrink-0">
            Retry
          </button>
        </div>
      ) : detail ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getAdjustmentStatusColor(detail.status ?? 'OPEN')}`}
            >
              {formatAdjustmentLabel(detail.status ?? 'OPEN')}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm bg-gray-50 rounded-lg p-4">
            <div>
              <span className="text-gray-500 block text-xs">Date</span>
              <span>{formatAdjustmentDate(detail.adjustment_date)}</span>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Account</span>
              <span className="font-medium">{detail.adjustment_account.name}</span>
            </div>
            {detail.memo && (
              <div>
                <span className="text-gray-500 block text-xs">Memo</span>
                <span>{detail.memo}</span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block text-xs">Created</span>
              <span>
                {formatAdjustmentDate(detail.created_at)}{' '}
                {formatAdjustmentTime(detail.created_at)}
              </span>
            </div>
            {detail.status === 'VOIDED' && detail.void_reason && (
              <div>
                <span className="text-gray-500 block text-xs">Void reason</span>
                <span>{detail.void_reason}</span>
              </div>
            )}
          </div>

          {detail.lines.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Line items</h4>
              <ul className="text-sm space-y-2">
                {detail.lines.map((line) => (
                  <li key={line.id} className="bg-gray-50 px-3 py-2 rounded">
                    <div className="font-medium">{line.item.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{line.item.sku}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {formatAdjustmentLabel(line.movement_type)} ·{' '}
                      {line.change_in_qty >= 0 ? '+' : ''}
                      {formatPreviewQuantity(line.change_in_qty)} ·{' '}
                      {formatCurrency(line.value_change)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-between text-sm font-semibold bg-gray-50 rounded-lg p-3">
            <span>Total value change</span>
            <span
              className={`font-mono ${totalValue >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              {formatCurrency(totalValue)}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
            {canVoidDetail && (
              <button
                type="button"
                onClick={openVoidModal}
                disabled={voiding}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
              >
                <Ban className="h-4 w-4" />
                Void
              </button>
            )}
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </TransactionOverlay>

    {detail && (
      <VoidConfirmationModal
        isOpen={showVoidModal}
        {...adjustmentVoidCopy(detail.adjustment_number)}
        confirmText="Void adjustment"
        showReasonField
        isSubmitting={voiding}
        error={voidError}
        onCancel={cancelVoidModal}
        onConfirm={confirmVoid}
      />
    )}
    </>
  );
};

export default InventoryAdjustmentPreview;
